/**
 * Workflow Toolbar Component
 *
 * Redesigned toolbar with:
 * - Grouped node palette (collapsible panel)
 * - Organized action buttons (primary/secondary)
 * - Better layout for scalability
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Code, X, History, ChevronDown, Plus, Save, Play, Upload, Download, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { nodeConfig } from './nodes';
import { useWorkflowStore, useIsDirty, useNodeCounts, useWorkflowMetadata, useEditorSettings, type WorkflowExportData } from '../hooks/useWorkflowStore';
import type { NodeType } from '@/types/imageWorkflow';
import { reactFlowIdToKey } from '@/types/imageWorkflow';
import { useI18n } from '@/i18n';

interface WorkflowToolbarProps {
  onSave?: () => void;
  onRun?: () => void;
  onHistory?: () => void;
  isSaving?: boolean;
  isRunning?: boolean;
  hasWorkflowId?: boolean;
}

// Node category IDs for grouping (labels come from i18n)
const NODE_CATEGORY_IDS = ['input', 'image', 'special', 'action', 'output'] as const;
type NodeCategoryId = typeof NODE_CATEGORY_IDS[number];

const NODE_CATEGORY_NODES: Record<NodeCategoryId, NodeType[]> = {
  input: ['prompt'],
  image: ['generate', 'edit', 'fuse'],
  special: ['lucky_charm'],
  action: ['push'],
  output: ['output'],
};

// JSON Preview Modal
const JsonPreviewModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const nodes = useWorkflowStore((s) => s.nodes);
  const edges = useWorkflowStore((s) => s.edges);
  const viewport = useWorkflowStore((s) => s.viewport);
  const metadata = useWorkflowMetadata();
  const settings = useEditorSettings();

  if (!isOpen) return null;

  const workflowJson: WorkflowExportData = {
    metadata: {
      id: metadata.id ?? undefined,
      key: metadata.key,
      name: metadata.name,
      description: metadata.description,
      version: metadata.version,
    },
    settings: {
      prompt_strategy: settings.promptStrategy,
      prompt_config: settings.promptConfig,
      storage_mode: settings.storageMode,
    },
    definition: {
      // Export uses node keys (without rf- prefix) for portability
      // The import will use these as keys and definitionToReactFlow will add rf- prefix
      nodes: nodes.map(node => {
        const nodeKey = node.data.nodeKey || reactFlowIdToKey(node.id);
        return {
          id: nodeKey,  // Use node key, not ReactFlow ID
          type: node.type ?? 'unknown',
          position: node.position,
          data: node.data as Record<string, unknown>,
        };
      }),
      edges: edges.map(edge => ({
        id: edge.id,
        source: reactFlowIdToKey(edge.source),  // Convert to node key
        target: reactFlowIdToKey(edge.target),  // Convert to node key
        sourceHandle: edge.sourceHandle ?? undefined,
        targetHandle: edge.targetHandle ?? undefined,
      })),
      viewport,
    },
  };

  const jsonString = JSON.stringify(workflowJson, null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonString);
  };

  const handleDownload = () => {
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow_${metadata.key || 'export'}_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-[800px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <Code className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-800">{t('imageWorkflows.toolbar.jsonExport.title')}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
            >
              {t('imageWorkflows.toolbar.jsonExport.copyToClipboard')}
            </button>
            <button
              onClick={handleDownload}
              className="px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded transition-colors flex items-center gap-1"
            >
              <Download className="w-4 h-4" />
              {t('imageWorkflows.toolbar.jsonExport.downloadJson')}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          <pre className="text-sm font-mono bg-gray-50 p-4 rounded-lg overflow-auto whitespace-pre">
            {jsonString}
          </pre>
        </div>
        {/* Footer */}
        <div className="px-4 py-3 border-t bg-gray-50 text-xs text-gray-500">
          💡 {t('imageWorkflows.toolbar.jsonExport.tip')}
        </div>
      </div>
    </div>
  );
};

// JSON Import Modal
const JsonImportModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importFromJson = useWorkflowStore((s) => s.importFromJson);

  const validateAndParse = useCallback((text: string): WorkflowExportData | null => {
    try {
      const data = JSON.parse(text);

      // Basic structure validation
      if (!data.metadata || !data.settings || !data.definition) {
        setError(t('imageWorkflows.toolbar.jsonImport.errors.missingFields'));
        return null;
      }

      if (!data.metadata.key || !data.metadata.name) {
        setError(t('imageWorkflows.toolbar.jsonImport.errors.missingMetadata'));
        return null;
      }

      if (!Array.isArray(data.definition.nodes) || !Array.isArray(data.definition.edges)) {
        setError(t('imageWorkflows.toolbar.jsonImport.errors.missingDefinition'));
        return null;
      }

      setError(null);
      return data as WorkflowExportData;
    } catch (e) {
      setError(t('imageWorkflows.toolbar.jsonImport.errors.parseError').replace('{error}', e instanceof Error ? e.message : 'Unknown'));
      return null;
    }
  }, [t]);

  const handleImport = useCallback(() => {
    const data = validateAndParse(jsonInput);
    if (data) {
      importFromJson(data);
      onClose();
      setJsonInput('');
    }
  }, [jsonInput, validateAndParse, importFromJson, onClose]);

  const handleFileSelect = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setJsonInput(text);
      validateAndParse(text);
    };
    reader.onerror = () => {
      setError(t('imageWorkflows.toolbar.jsonImport.errors.fileReadError'));
    };
    reader.readAsText(file);
  }, [validateAndParse, t]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/json') {
      handleFileSelect(file);
    } else {
      setError(t('imageWorkflows.toolbar.jsonImport.errors.dropJsonFile'));
    }
  }, [handleFileSelect, t]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-[700px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-800">{t('imageWorkflows.toolbar.jsonImport.title')}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* File Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
              transition-all
              ${isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
              }
            `}
          >
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600">
              {t('imageWorkflows.toolbar.jsonImport.dropHint')}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>

          {/* Or paste directly */}
          <div className="text-center text-sm text-gray-400">{t('imageWorkflows.toolbar.jsonImport.orPaste')}</div>

          {/* JSON Input */}
          <textarea
            value={jsonInput}
            onChange={(e) => {
              setJsonInput(e.target.value);
              if (e.target.value) {
                validateAndParse(e.target.value);
              } else {
                setError(null);
              }
            }}
            placeholder={t('imageWorkflows.toolbar.jsonImport.placeholder')}
            className={`
              w-full h-48 p-3 font-mono text-sm border rounded-lg resize-none
              focus:outline-none focus:ring-2
              ${error
                ? 'border-red-300 focus:ring-red-200'
                : 'border-gray-300 focus:ring-blue-200 focus:border-blue-400'
              }
            `}
          />

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
          <span className="text-xs text-gray-500">
            ⚠️ {t('imageWorkflows.toolbar.jsonImport.tip')}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('imageWorkflows.toolbar.jsonImport.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleImport}
              disabled={!jsonInput || !!error}
            >
              {t('imageWorkflows.toolbar.jsonImport.import')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Draggable node item
const DraggableNode: React.FC<{ type: NodeType; disabled?: boolean; compact?: boolean }> = ({
  type,
  disabled = false,
  compact = false
}) => {
  const { t } = useI18n();
  const config = nodeConfig[type];

  const handleDragStart = (event: React.DragEvent) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.setData('application/workflow-node', type);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      draggable={!disabled}
      onDragStart={handleDragStart}
      className={`
        flex items-center gap-2 rounded-md border-2 transition-all
        ${config.bgColor} ${config.borderColor}
        ${disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-grab hover:shadow-md active:cursor-grabbing'}
        ${compact ? 'px-2 py-1.5' : 'px-3 py-2'}
      `}
      title={t('imageWorkflows.toolbar.dragHint').replace('{label}', config.label)}
    >
      <span className={config.color}>{config.icon}</span>
      <span className={`text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    </div>
  );
};

// Node Palette Panel (Dropdown with categories)
const NodePalettePanel: React.FC<{ disabled?: boolean }> = ({ disabled = false }) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['input', 'image', 'special', 'action', 'output']);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed
          transition-all font-medium text-sm
          ${disabled
            ? 'border-gray-200 text-gray-400 cursor-not-allowed'
            : 'border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50'}
        `}
      >
        <Plus className="w-4 h-4" />
        <span>{t('imageWorkflows.toolbar.addNode')}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && !disabled && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
          {/* Panel Header */}
          <div className="px-4 py-3 bg-gray-50 border-b">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-800">{t('imageWorkflows.toolbar.nodePanel')}</span>
              <span className="text-xs text-gray-500">{t('imageWorkflows.toolbar.dragToAdd')}</span>
            </div>
          </div>

          {/* Categories */}
          <div className="max-h-[400px] overflow-y-auto">
            {NODE_CATEGORY_IDS.map((categoryId) => (
              <div key={categoryId} className="border-b border-gray-100 last:border-b-0">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(categoryId)}
                  className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex flex-col items-start">
                    <span className="font-medium text-gray-700 text-sm">{t(`imageWorkflows.toolbar.categories.${categoryId}.label`)}</span>
                    <span className="text-xs text-gray-400">{t(`imageWorkflows.toolbar.categories.${categoryId}.description`)}</span>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${
                      expandedCategories.includes(categoryId) ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Category Nodes */}
                {expandedCategories.includes(categoryId) && (
                  <div className="px-4 pb-3 flex flex-wrap gap-2">
                    {NODE_CATEGORY_NODES[categoryId].map((nodeType) => (
                      <DraggableNode key={nodeType} type={nodeType} compact />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Panel Footer */}
          <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">
            💡 {t('imageWorkflows.toolbar.startNodeTip')}
          </div>
        </div>
      )}
    </div>
  );
};

export const WorkflowToolbar: React.FC<WorkflowToolbarProps> = ({
  onSave,
  onRun,
  onHistory,
  isSaving = false,
  isRunning = false,
  hasWorkflowId = false,
}) => {
  const { t } = useI18n();
  const isDirty = useIsDirty();
  const nodeCounts = useNodeCounts();
  const workflowKey = useWorkflowStore((s) => s.workflowKey);
  const workflowName = useWorkflowStore((s) => s.workflowName);
  const setWorkflowKey = useWorkflowStore((s) => s.setWorkflowKey);
  const setWorkflowName = useWorkflowStore((s) => s.setWorkflowName);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [showJsonImport, setShowJsonImport] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);

  // Validate workflow key
  const validateKey = (key: string): string | null => {
    if (!key) return null; // Empty is allowed during editing, checked on save
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      return t('imageWorkflows.toolbar.keyValidation.startWithLowercase');
    }
    if (key.length > 64) {
      return t('imageWorkflows.toolbar.keyValidation.maxLength');
    }
    return null;
  };

  const handleKeyChange = (value: string) => {
    // Auto-convert to lowercase and replace invalid chars
    const normalized = value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    setWorkflowKey(normalized);
    setKeyError(validateKey(normalized));
  };

  // Warn user before leaving if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = t('imageWorkflows.toolbar.unsavedWarning');
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, t]);

  return (
    <div className="border-b bg-white">
      {/* Main Toolbar */}
      <div className="px-4 py-3 flex items-center justify-between">
        {/* Left: Workflow Info + Node Palette */}
        <div className="flex items-center gap-4">
          {/* Workflow Name & Key */}
          <div className="flex items-center gap-3">
            {/* Name */}
            <input
              type="text"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              disabled={isRunning}
              className={`
                text-lg font-semibold border-0 border-b-2 border-transparent
                hover:border-gray-200 focus:border-blue-500 focus:outline-none
                bg-transparent min-w-[160px]
                ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              placeholder={t('imageWorkflows.toolbar.workflowName')}
            />
            {/* Key */}
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400">Key:</span>
                <input
                  type="text"
                  value={workflowKey}
                  onChange={(e) => handleKeyChange(e.target.value)}
                  disabled={isRunning}
                  className={`
                    text-sm font-mono border-0 border-b-2
                    focus:outline-none bg-transparent w-[120px]
                    ${keyError
                      ? 'border-red-300 focus:border-red-500 text-red-600'
                      : 'border-transparent hover:border-gray-200 focus:border-blue-500 text-blue-600'
                    }
                    ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  placeholder={t('imageWorkflows.toolbar.workflowKey')}
                />
              </div>
              {keyError && (
                <span className="text-xs text-red-500 mt-0.5">{keyError}</span>
              )}
            </div>
            {isDirty && (
              <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full font-medium">
                {t('imageWorkflows.toolbar.unsaved')}
              </span>
            )}
          </div>

          {/* Separator */}
          <div className="h-6 w-px bg-gray-200" />

          {/* Node Palette */}
          <NodePalettePanel disabled={isRunning} />

          {/* Node Count */}
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
            {t('imageWorkflows.toolbar.nodeCount').replace('{count}', String(nodeCounts.total))}
          </span>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Secondary Actions */}
          <div className="flex items-center gap-1 pr-2 border-r border-gray-200">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowJsonImport(true)}
              disabled={isRunning}
              title={t('imageWorkflows.toolbar.importJson')}
            >
              <Upload className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowJsonPreview(true)}
              disabled={nodeCounts.total === 0}
              title={t('imageWorkflows.toolbar.exportJson')}
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onHistory}
              disabled={!hasWorkflowId}
              title={!hasWorkflowId ? t('imageWorkflows.toolbar.saveFirst') : t('imageWorkflows.toolbar.executionHistory')}
            >
              <History className="w-4 h-4" />
            </Button>
          </div>

          {/* Primary Actions */}
          <Button
            variant="secondary"
            size="sm"
            onClick={onSave}
            disabled={isSaving || !isDirty}
          >
            {isSaving ? (
              <>
                <span className="animate-spin mr-1.5">⟳</span>
                {t('imageWorkflows.toolbar.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-1.5" />
                {t('imageWorkflows.toolbar.save')}
              </>
            )}
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={onRun}
            disabled={isRunning || nodeCounts.total === 0}
          >
            {isRunning ? (
              <>
                <span className="animate-spin mr-1.5">⟳</span>
                {t('imageWorkflows.toolbar.running')}
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-1.5" />
                {t('imageWorkflows.toolbar.run')}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* JSON Preview Modal (Export) */}
      <JsonPreviewModal isOpen={showJsonPreview} onClose={() => setShowJsonPreview(false)} />

      {/* JSON Import Modal */}
      <JsonImportModal isOpen={showJsonImport} onClose={() => setShowJsonImport(false)} />
    </div>
  );
};

export default WorkflowToolbar;
